import { useEffect, useRef } from "react";

import {
    createExpressionContext,
    evaluateExpressionSafe,
} from "./expression-sandbox";
import { EXPRESSION_SOURCE } from "./design-fault-reporting";
import { applyNumberPrecision, normalizeComputed } from "./compute-value";
import {
    checkComputedValueType,
    formatTypeMismatch,
} from "./computed-type-check";
import { buildItemTypeMap } from "./form-helpers";
import { METHOD_TYPE } from "./method-types";
import { logTypeMismatch } from "./type-mismatch-reporting";

/**
 * Returns true when the item value is considered "empty" and a default should
 * be applied.  Mirrors the default-value logic used by form-visibility.js:
 * - null / undefined / "" → empty
 * - [] (Checkboxes with no selection) → empty
 * - 0, false, and any other truthy/falsy scalar → NOT empty (valid user input)
 */
function isEmpty(value) {
    if (value === null || value === undefined || value === "") {
        return true;
    }
    if (Array.isArray(value) && value.length === 0) {
        return true;
    }
    return false;
}

/**
 * Applies `MethodDef` Imputation defaults to form items.
 *
 * For each item in `form.itemGroups[].items` that has:
 *   - `item.methodDef.methodType === 1` (MethodType.Imputation — numeric enum value 1)
 *   - `item.isVisible !== false` — hidden items are skipped, they get a fresh
 *     try when visibility flips on
 *   - a current value in `formData` that is empty (null / undefined / "" / [])
 *   - an expression that evaluates to a non-undefined value with the current
 *     `formData` / `dependencies` / `identifierModel`
 *
 * …evaluates `item.methodDef.expression` via the shared sandbox, then calls
 * both `setValue` (RHF) and `setItemValue` (reducer dispatch) so visibility
 * expressions and other formData-derived state see the default value.
 *
 * Contract:
 *   - Each item's default fires at most once per form-definition. A SET_FORM
 *     (fresh `itemGroups` identity) clears the tracker; a subsequent
 *     SET_ITEM_VALUE / SET_DEPENDENCY does not.
 *   - A default that evaluates to `undefined` (e.g. cross-form dependency
 *     not yet provided) does NOT mark the item as applied — the next render
 *     will retry. Once a non-undefined value is written (or the user types
 *     one manually), the item is locked.
 *   - A hidden item never fires until visibility turns on. This matches
 *     the live designer scenario where the default-bearing item is gated
 *     behind a parent radio.
 *
 * @param {{
 *   form: {
 *     itemGroups: Array<{ items: Array<{ oid: string, isVisible?: boolean, methodDef?: { methodType: number, expression: string } }> }>,
 *     formData: object,
 *     dependencies: object,
 *     identifierModel: object,
 *   },
 *   setValue: (oid: string, value: *) => void,
 *   setItemValue: (oid: string, value: *, format?: *) => void,
 *   readOnly: boolean,
 *   onTypeMismatch?: (mismatch: {
 *     oid: string, label?: string, itemType?: string, expression: string,
 *     expected: string, actual: string, source: "default", message: string,
 *   }) => void,
 * }} params
 */
export function useDefaults({
    form,
    setValue,
    setItemValue,
    readOnly,
    onTypeMismatch,
}) {
    // Per-item flag: defaults have been applied (or short-circuited because
    // the user already typed a value) for this item within the current
    // form-definition. Reset when itemGroups identity changes.
    const appliedItemsRef = useRef(new Set());
    const lastItemGroupsRef = useRef(null);

    // Held in a ref, not listed as an effect dependency: callers pass an inline
    // arrow (the common case), which would otherwise re-run the effect on every
    // render. The per-item lock below already prevents a repeat report, so this
    // is about not re-arming the effect rather than about dedupe.
    const onTypeMismatchRef = useRef(onTypeMismatch);
    onTypeMismatchRef.current = onTypeMismatch;

    useEffect(() => {
        // In readOnly mode (viewing a submitted form) leave stored values
        // as-is — applying an Imputation default would display a value that
        // was never submitted.
        if (readOnly) {
            return;
        }

        if (!form?.itemGroups?.length) {
            appliedItemsRef.current = new Set();
            lastItemGroupsRef.current = null;
            return;
        }

        if (form.itemGroups !== lastItemGroupsRef.current) {
            appliedItemsRef.current = new Set();
            lastItemGroupsRef.current = form.itemGroups;
        }

        const context = createExpressionContext({
            formData: form.formData ?? {},
            dependencies: form.dependencies ?? {},
            identifierModel: form.identifierModel ?? {},
            itemTypes: buildItemTypeMap(form),
        });

        for (const itemGroup of form.itemGroups) {
            for (const item of itemGroup.items ?? []) {
                const { methodDef, oid } = item;

                if (!methodDef || methodDef.methodType !== METHOD_TYPE.Imputation) {
                    continue;
                }
                // Skip the default-placeholder MethodDef (methodType=1, expression=null)
                // that Format.Cdisc 2.20.0 emits for every item with no ImputationMethodOID.
                if (!methodDef.expression) {
                    continue;
                }
                // Hidden items: leave for a later render. updateVisibility
                // sets isVisible on each item; an undefined value means
                // "no visibility tracked yet" (test fixture / pre-render),
                // which we treat as visible so the existing test surface
                // keeps working. Hide-always items stay skipped too, unlike
                // in the recompute hook: the server never executes Imputation
                // methods, so there is no background default to mirror.
                if (item.isVisible === false) {
                    continue;
                }
                if (appliedItemsRef.current.has(oid)) {
                    continue;
                }

                const currentValue = form.formData?.[oid];
                if (!isEmpty(currentValue)) {
                    // Pre-existing value (user typed, server-supplied, or
                    // we already applied earlier in this render pass).
                    // Lock the item so we don't re-fire if the user later
                    // clears the field.
                    appliedItemsRef.current.add(oid);
                    continue;
                }

                const computed = evaluateExpressionSafe(
                    methodDef.expression,
                    context,
                    undefined,
                    {
                        source: EXPRESSION_SOURCE.DEFAULT_VALUE,
                        oid,
                        label: item.label,
                    }
                );

                // Expression couldn't yield a value (e.g. sidebar dep still
                // empty). Don't lock — try again next render once the deps
                // arrive.
                if (computed === undefined) {
                    continue;
                }

                // normalizeComputed maps NaN/±Infinity (and undefined) to
                // undefined, so a non-finite default is skipped rather than
                // written as garbage. An invalid Date also maps to undefined
                // there, because date-fns format() throws on one and this call
                // is not wrapped.
                const normalized = normalizeComputed(computed);

                // Type gate, same rule set as useFunctionRecompute. An
                // Imputation default is the ONLY line of defence for its item:
                // FormDataSaveService.ExecuteFunctionsAsync re-executes
                // computations server-side but selects on MethodOID, and ODM
                // carries defaults under ImputationMethodOID — so a bad default
                // is never corrected later.
                //
                // An invalid Date is the one case where the raw result says
                // more than the normalized one (see above), so the gate is
                // given the raw value there.
                const isInvalidDate =
                    computed instanceof Date && Number.isNaN(computed.getTime());
                const typeCheck = checkComputedValueType(
                    isInvalidDate ? computed : normalized,
                    item.itemType
                );

                if (!typeCheck.ok) {
                    const mismatch = {
                        oid,
                        label: item.label,
                        itemType: item.itemType,
                        expression: methodDef.expression,
                        expected: typeCheck.expected,
                        actual: typeCheck.actual,
                        source: "default",
                    };
                    const message = formatTypeMismatch(mismatch);
                    onTypeMismatchRef.current?.({ ...mismatch, message });
                    // No dedupe needed, unlike the recompute path: a default
                    // fires at most once per form definition because of the
                    // lock below, so there is no per-keystroke repeat to
                    // suppress.
                    logTypeMismatch(mismatch, message);

                    // Do NOT write. Unlike a Computation there is nothing to
                    // clear — the item is empty by definition here (isEmpty
                    // above), so leaving it alone IS the correct outcome.
                    //
                    // Locked anyway: the expression produced a value, just an
                    // unusable one, so retrying every render would only repeat
                    // the report. Matches the "produced something -> lock"
                    // half of the contract above.
                    appliedItemsRef.current.add(oid);
                    continue;
                }

                if (normalized !== undefined) {
                    // Same Number-decimals treatment as useFunctionRecompute:
                    // round the default to the designer decimals and store the
                    // setting as the __format sidecar.
                    const { value: storedValue, format } = applyNumberPrecision(
                        normalized,
                        item
                    );
                    setValue(oid, storedValue);
                    setItemValue(oid, storedValue, format);
                }

                appliedItemsRef.current.add(oid);
            }
        }
    }, [form, setValue, setItemValue, readOnly]);
}
